import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FAQItem } from 'components/molecules';
import { Section } from 'components/core/Page';
import { H2 } from 'components/core/Typography';
import './FacilityAmenitiesAccordion.scss';

// Helper function to check if a category has attributes
const categoryHasAttributes = (featuresData, categoryKey) =>
	featuresData[categoryKey]?.hasAttributes === true;

// Helper function to create feature content sections
const createFeatureContent = (featuresData, categories) =>
	categories
		.filter(({ key }) => categoryHasAttributes(featuresData, key))
		.map(({ key, title: sectionTitle }) => ({
			title: sectionTitle,
			content: featuresData[key]?.items || [],
		}));

// Helper function to safely decode and format description for markdown rendering
const getFormattedDescription = (description) => {
	if (!description) return 'Facility description not available.';

	const formatText = (text) =>
		text
			.replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '\n\n')
			.replace(/<br\s*\/?>/gi, '\n\n')
			.replace(/\n+/g, '\n\n')
			.trim();

	try {
		return formatText(decodeURIComponent(description));
	} catch (error) {
		return formatText(description);
	}
};

export const FacilityAmenitiesAccordion = (props) => {
	const { featuresDataByCategory = {}, title, name, description, hours } = props;
	const [openAccordions, setOpenAccordions] = useState(new Set());

	// Facility Description Accordian Content
	const feature0 = useMemo(
		() => ({
			content: [
				{
					question: 'Facility Description',
					answer: `**About ${name}**\n\n${getFormattedDescription(description)}`,
				},
			],
		}),
		[name, description],
	);

	const feature1 = useMemo(
		() => ({
			content: [
				{
					question: 'Office & Gate Access Hours',
					answer: {
						accessHours: hours?.access,
						officeHours: hours?.office,
					},
				},
			],
		}),
		[hours],
	);

	// Create feature2 only if either access or security has true attributes
	const feature2 = useMemo(() => {
		const feature2Content = createFeatureContent(featuresDataByCategory, [
			{ key: 'access', title: 'Access' },
			{ key: 'security', title: 'Security' },
		]);
		return feature2Content.length > 0
			? {
					content: [
						{
							question: 'Security & Facility Protections',
							answer: feature2Content,
						},
					],
				}
			: null;
	}, [featuresDataByCategory.access, featuresDataByCategory.security]);

	// Create feature3 only if either vehicle requirements or property coverage has true attributes
	const feature3 = useMemo(() => {
		const feature3Content = createFeatureContent(featuresDataByCategory, [
			{
				key: 'vehicleStorageRequirements',
				title: 'Vehicle Storage Requirements',
			},
			{ key: 'propertyCoverage', title: 'Property Coverage' },
		]);
		return feature3Content.length > 0
			? {
					content: [
						{
							question: 'Vehicle Storage Options & Coverage',
							answer: feature3Content,
						},
					],
				}
			: null;
	}, [
		featuresDataByCategory.vehicleStorageRequirements,
		featuresDataByCategory.propertyCoverage,
	]);

	// Create feature4 only if any of payments, billing, or miscellaneous has true attributes
	const feature4 = useMemo(() => {
		const feature4Content = createFeatureContent(featuresDataByCategory, [
			{ key: 'payments', title: 'Payments' },
			{ key: 'billing', title: 'Billing' },
			{ key: 'miscellaneous', title: 'Miscellaneous' },
		]);
		return feature4Content.length > 0
			? {
					content: [
						{
							question: 'Billing & Payment Options',
							answer: feature4Content,
						},
					],
				}
			: null;
	}, [
		featuresDataByCategory.payments,
		featuresDataByCategory.billing,
		featuresDataByCategory.miscellaneous,
	]);

	// Create feature5 only if Premium Amenities & Services has any true attributes
	const feature5 = useMemo(
		() =>
			categoryHasAttributes(featuresDataByCategory, 'premiumServices')
				? {
						content: [
							{
								question: 'Premium Amenities & Services',
								answer: [
									{
										content:
											featuresDataByCategory
												.premiumServices?.items || [],
									},
								],
							},
						],
					}
				: null,
		[featuresDataByCategory.premiumServices],
	);

	// Safety check - don't render anything if no facility data
	if (!Object.keys(featuresDataByCategory).length) {
		return null;
	}

	const handleToggleAccordion = (featureKey) => {
		setOpenAccordions((prev) => {
			const next = new Set(prev);
			if (next.has(featureKey)) {
				next.delete(featureKey);
			} else {
				next.add(featureKey);
			}
			return next;
		});
	};

	return (
		<>
			<noscript>
				<style>{`
					.facility-amenities-accordion .expand-content {
						display: block !important;
						height: auto !important;
						overflow: visible !important;
					}
					.facility-amenities-accordion .expand-button .sf-icon {
						visibility: hidden !important;
					}
					.facility-amenities-accordion .expand-button {
						cursor: default !important;
						pointer-events: none !important;
					}
				`}</style>
			</noscript>
			<Section className="facility-amenities-accordion-wrapper">
			<H2
				className="facility-amenities-accordion-header"
			>
				{title || 'Facility Amenities & Features'}
			</H2>
			<div className="facility-amenities-accordion">
				{description && (
					<FAQItem
						{...feature0}
						iconType="expand"
						openItemIndex={openAccordions.has('feature0') ? 0 : -1}
						onToggleItem={() => handleToggleAccordion('feature0')}
					/>
				)}
				{feature2 && (
					<FAQItem
						{...feature2}
						type="facility"
						iconType="expand"
						openItemIndex={openAccordions.has('feature2') ? 0 : -1}
						onToggleItem={() => handleToggleAccordion('feature2')}
					/>
				)}
				<FAQItem
					{...feature1}
					type="accessHours"
					iconType="expand"
					openItemIndex={openAccordions.has('feature1') ? 0 : -1}
					onToggleItem={() => handleToggleAccordion('feature1')}
				/>
				{feature3 && (
					<FAQItem
						{...feature3}
						type="facility"
						iconType="expand"
						openItemIndex={openAccordions.has('feature3') ? 0 : -1}
						onToggleItem={() => handleToggleAccordion('feature3')}
					/>
				)}
				{feature4 && (
					<FAQItem
						{...feature4}
						type="facility"
						iconType="expand"
						openItemIndex={openAccordions.has('feature4') ? 0 : -1}
						onToggleItem={() => handleToggleAccordion('feature4')}
					/>
				)}
				{feature5 && (
					<FAQItem
						{...feature5}
						type="facility"
						iconType="expand"
						openItemIndex={openAccordions.has('feature5') ? 0 : -1}
						onToggleItem={() => handleToggleAccordion('feature5')}
					/>
				)}
			</div>
		</Section>
		</>
	);
};

FacilityAmenitiesAccordion.propTypes = {
	featuresDataByCategory: PropTypes.object.isRequired,
	name: PropTypes.string,
	description: PropTypes.string,
	hours: PropTypes.shape({
		access: PropTypes.shape({
			text: PropTypes.string,
		}),
		office: PropTypes.shape({
			text: PropTypes.string,
		}),
	}).isRequired,
	title: PropTypes.string,
};

export default FacilityAmenitiesAccordion;
